// ============================================
// src/components/payments/PaymentList.js
// Enhanced with inline edit functionality
// ============================================

import React, { useState } from 'react';
import { supabase } from '../../config/supabase.config';

function PaymentList({ payments, residents, currentMonth, currentYear, onMonthChange, onRefresh }) {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPayment, setEditingPayment] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Get resident details for display
  const getResidentDetails = (residentId) => {
    const resident = residents.find(r => r.id === residentId);
    return resident || { name: 'Unknown', flat_number: '-' };
  };

  // Filter and search logic
  const filteredPayments = payments.filter(payment => {
    const resident = payment.residents || getResidentDetails(payment.resident_id);
    
    // Status filter
    if (filterStatus === 'paid' && payment.status !== 'paid') return false;
    if (filterStatus === 'pending' && payment.status === 'paid') return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        resident.name?.toLowerCase().includes(query) ||
        resident.flat_number?.toLowerCase().includes(query) ||
        payment.transaction_ref?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Get all residents with payment status
  const residentsWithStatus = residents.map(resident => {
    const payment = payments.find(p => p.resident_id === resident.id);
    return {
      ...resident,
      payment: payment || null,
      status: payment ? 'paid' : 'pending'
    };
  });

  // Group residents by flat for status view
  const flatStatusMap = {};
  residentsWithStatus.forEach(resident => {
    if (!flatStatusMap[resident.flat_number]) {
      flatStatusMap[resident.flat_number] = {
        flat_number: resident.flat_number,
        residents: [],
        status: 'pending'
      };
    }
    flatStatusMap[resident.flat_number].residents.push(resident);
    if (resident.status === 'paid') {
      flatStatusMap[resident.flat_number].status = 'paid';
    }
  });

  const flatsArray = Object.values(flatStatusMap).sort((a, b) => 
    a.flat_number.localeCompare(b.flat_number, undefined, { numeric: true, sensitivity: 'base' })
  );

  const handleEditClick = (payment) => {
    setEditingPayment(payment.id);
    setEditFormData({
      amount: payment.amount,
      payment_date: payment.payment_date,
      payment_method: payment.payment_method,
      transaction_ref: payment.transaction_ref || '',
      status: payment.status,
      notes: payment.notes || ''
    });
  };

  const handleEditSave = async (paymentId) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('payments')
        .update({
          amount: parseFloat(editFormData.amount),
          payment_date: editFormData.payment_date,
          payment_method: editFormData.payment_method,
          transaction_ref: editFormData.transaction_ref,
          status: editFormData.status,
          notes: editFormData.notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) throw error;

      alert('Payment updated successfully');
      setEditingPayment(null);
      setEditFormData({});
      onRefresh();
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingPayment(null);
    setEditFormData({});
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    
    try {
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', paymentId);
      
      if (error) throw error;
      
      alert('Payment deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment');
    }
  };

  const generateReceipt = (payment) => {
    const resident = payment.residents || getResidentDetails(payment.resident_id);
    
    const receiptContent = `
========================================
MAINTENANCE RECEIPT
========================================
Receipt No: ${payment.receipt_number || 'N/A'}
Date: ${new Date(payment.payment_date).toLocaleDateString()}

Resident: ${resident.name}
Flat: ${resident.flat_number}

Month: ${payment.payment_month}/${payment.payment_year}
Amount: ₹${payment.amount}
Late Fee: ₹${payment.late_fee || 0}
Total: ₹${payment.amount}

Payment Method: ${payment.payment_method?.toUpperCase()}
Transaction Ref: ${payment.transaction_ref || 'N/A'}

Status: ${payment.status?.toUpperCase()}
========================================
Thank you for your payment!
========================================
    `;
    
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receipt_${payment.receipt_number || payment.id}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ color: '#333', marginBottom: '15px' }}>Payment Records</h3>
        
        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <input
            type="text"
            placeholder="Search by name, flat, or transaction..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              padding: '10px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '10px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'white'
            }}
          >
            <option value="all">All Status</option>
            <option value="paid">Paid Only</option>
            <option value="pending">Pending Only</option>
          </select>
          
          <button
            onClick={onRefresh}
            style={{
              padding: '10px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Quick Status View */}
      <div style={{ marginBottom: '30px' }}>
        <h4 style={{ color: '#555', marginBottom: '10px' }}>Quick Status View</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
          {flatsArray.map(flat => (
            <div
              key={flat.flat_number}
              style={{
                padding: '10px',
                background: flat.status === 'paid' ? '#d4edda' : '#f8d7da',
                borderRadius: '8px',
                textAlign: 'center',
                border: `2px solid ${flat.status === 'paid' ? '#c3e6cb' : '#f5c6cb'}`
              }}
            >
              <div style={{ fontWeight: 'bold' }}>{flat.flat_number}</div>
              <div style={{ fontSize: '12px', marginTop: '5px' }}>
                {flat.residents.map(r => r.name).join(' & ')}
              </div>
              <div style={{ 
                fontSize: '10px', 
                marginTop: '5px',
                color: flat.status === 'paid' ? '#155724' : '#721c24'
              }}>
                {flat.status === 'paid' ? '✅ PAID' : '⏳ PENDING'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payments Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={tableHeaderStyle}>Flat</th>
              <th style={tableHeaderStyle}>Name</th>
              <th style={tableHeaderStyle}>Amount</th>
              <th style={tableHeaderStyle}>Date</th>
              <th style={tableHeaderStyle}>Method</th>
              <th style={tableHeaderStyle}>Transaction Ref</th>
              <th style={tableHeaderStyle}>Status</th>
              <th style={tableHeaderStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length > 0 ? (
              filteredPayments.map(payment => {
                const resident = payment.residents || getResidentDetails(payment.resident_id);
                const isEditing = editingPayment === payment.id;
                
                return (
                  <tr key={payment.id}>
                    <td style={tableCellStyle}>{resident.flat_number}</td>
                    <td style={tableCellStyle}>{resident.name}</td>
                    <td style={tableCellStyle}>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editFormData.amount}
                          onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
                          style={{ width: '80px', padding: '4px' }}
                        />
                      ) : (
                        `₹${payment.amount}`
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editFormData.payment_date}
                          onChange={(e) => setEditFormData({...editFormData, payment_date: e.target.value})}
                          style={{ padding: '4px' }}
                        />
                      ) : (
                        new Date(payment.payment_date).toLocaleDateString()
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {isEditing ? (
                        <select
                          value={editFormData.payment_method}
                          onChange={(e) => setEditFormData({...editFormData, payment_method: e.target.value})}
                          style={{ padding: '4px' }}
                        >
                          <option value="gpay">GPay</option>
                          <option value="phonepe">PhonePe</option>
                          <option value="paytm">Paytm</option>
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="upi">Other UPI</option>
                        </select>
                      ) : (
                        <span style={{
                          padding: '3px 8px',
                          background: '#e3f2fd',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {payment.payment_method?.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editFormData.transaction_ref}
                          onChange={(e) => setEditFormData({...editFormData, transaction_ref: e.target.value})}
                          style={{ width: '120px', padding: '4px' }}
                        />
                      ) : (
                        payment.transaction_ref || '-'
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {isEditing ? (
                        <select
                          value={editFormData.status}
                          onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                          style={{ padding: '4px' }}
                        >
                          <option value="paid">Paid</option>
                          <option value="pending">Pending</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      ) : (
                        <span style={{
                          padding: '3px 8px',
                          background: payment.status === 'paid' ? '#28a745' : '#ffc107',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {payment.status?.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleEditSave(payment.id)}
                            disabled={saving}
                            style={{
                              padding: '5px 10px',
                              margin: '2px',
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: saving ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {saving ? 'Saving...' : '💾 Save'}
                          </button>
                          <button
                            onClick={handleEditCancel}
                            style={{
                              padding: '5px 10px',
                              margin: '2px',
                              background: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ❌ Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => generateReceipt(payment)}
                            style={{
                              padding: '5px 10px',
                              margin: '2px',
                              background: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            📄 Receipt
                          </button>
                          <button
                            onClick={() => handleEditClick(payment)}
                            style={{
                              padding: '5px 10px',
                              margin: '2px',
                              background: '#ffc107',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDelete(payment.id)}
                            style={{
                              padding: '5px 10px',
                              margin: '2px',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" style={{ ...tableCellStyle, textAlign: 'center', padding: '20px' }}>
                  No payments found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const tableHeaderStyle = {
  padding: '12px',
  textAlign: 'left',
  borderBottom: '2px solid #dee2e6',
  fontWeight: '600',
  color: '#495057'
};

const tableCellStyle = {
  padding: '10px',
  borderBottom: '1px solid #dee2e6'
};

export default PaymentList;